import { AlertCircle, Check, Eye, Truck } from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type {
    OcrAdHocReadingDto, OcrCandidateDto, OcrEvidenceDto, OcrMatchDto, SuggestLoadDto,
} from '@/api/types';

/**
 * "THIS PAGE LOOKS LIKE LOAD X" — WHAT OCR FOUND, AND THE ONE CLICK TO ACCEPT IT.
 *
 * ---- ⭐ ONE CLICK, NEVER ZERO ----------------------------------------------
 *
 * Pete, 2026-08-25: *"One-click is enough with maybe an eye icon to double check
 * if necessary."*
 *
 * ⭐ THE EYE IS WHAT MAKES ONE CLICK SAFE. Accepting without being able to look
 * at the page is a reflex; accepting after glancing at it is a decision. The two
 * cost the same when the machine is right, and only one of them catches it when
 * the machine is wrong.
 *
 * ⛔ AND NOTHING ATTACHES ITSELF. An automatic attach is the inference #212
 * deliberately removed from the kiosk — the whole redesign was that a PERSON
 * makes the link between a truck and a load.
 *
 * ---- ⛔ IT NAMES THE EVIDENCE, NEVER A SCORE -------------------------------
 *
 * "Matched on the container number CSQU3054383" can be checked in two seconds
 * against the paper in the scheduler's hand. "92% confident" cannot be checked at
 * all, so it is either trusted blindly or ignored. Same rule as
 * `MatchArrivalDialog`, and for the same reason.
 *
 * ⚠️ A REPAIRED READING SAYS SO. A container recovered from an OCR misread is a
 * lower grade of evidence than one that read cleanly, and the scheduler is told
 * which they are looking at — the same doctrine as the rebuilt licence barcode
 * and `IsManualEntry`.
 */

interface Props {
    open: boolean;
    result: SuggestLoadDto | null;
    /** The document this was read from — the eye opens it. */
    documentName: string;
    onView: () => void;
    onAttach: (appointmentId: string) => void;
    onClose: () => void;
}

/** ⚠️ The server's words for what matched, in the scheduler's. */
const KIND_LABEL: Record<OcrCandidateDto['kind'], string> = {
    appointment: 'the appointment number',
    container: 'the container number',
    bol: 'the BOL number',
    po: 'the PO number',
    pro: 'the PRO number',
    trailer: 'the trailer number',
};

/**
 * ⭐ AN AD-HOC FIELD IS NAMED BY ITS LABEL AND SAYS SO (#248).
 *
 * ⚠️ AND IT SAYS WHERE IT LOOKED. A scheduler who sees "matched on Job No" has no
 * way to check that against the paper in their hand unless they also know the
 * number was looked up as a reference. The whole doctrine of this dialog is that
 * evidence must be checkable in two seconds.
 */
function AdHocEvidence({ on }: { on: OcrAdHocReadingDto }) {
    return (
        <div className="rounded border bg-muted/40 px-2 py-1.5 text-xs">
            <div>
                Read <span className="font-medium">{on.label}</span>{' '}
                <span className="font-mono">{on.value}</span> off the page, beside its label.
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
                That is one of this client&rsquo;s configured fields, looked up as the{' '}
                {COLUMN_WORDS[on.column] ?? on.column}.
            </div>
        </div>
    );
}

/** ⚠️ Plain words, never a schema column name, on a screen a scheduler reads. */
const COLUMN_WORDS: Record<string, string> = {
    AppointmentNumber: 'appointment number',
    ContainerNumber: 'container number',
    BolNumber: 'BOL number',
    PoNumber: 'PO number',
    ReferenceNumber: 'reference number',
    AsnNumber: 'ASN number',
};

function Evidence({ on }: { on: OcrCandidateDto }) {
    return (
        <div className="rounded border bg-muted/40 px-2 py-1.5 text-xs">
            <div>
                Read <span className="font-medium">{KIND_LABEL[on.kind]}</span>{' '}
                <span className="font-mono">{on.value}</span> off the page
                {on.viaLabel ? ', beside its label' : ''}.
            </div>
            {/**
              * ⭐ THE CHECK DIGIT IS WORTH SAYING OUT LOUD. A container is the only
              * identifier here that proves itself without a lookup, and a scheduler
              * who knows that treats it differently from a PO — correctly.
              */}
            {on.kind === 'container' && on.grade === 'proven' && (
                <div className="mt-0.5 text-[11px] text-emerald-700">
                    Its check digit is valid, so the number itself is confirmed.
                </div>
            )}
            {on.grade === 'recovered' && (
                <div className="mt-0.5 text-[11px] text-amber-800">
                    ⚠ The page read as <span className="font-mono">{on.readAs}</span> and was
                    repaired to a valid number. Worth a glance before accepting.
                </div>
            )}
        </div>
    );
}

/** ⛔ ONE PLACE THAT KNOWS THE TWO SHAPES OF EVIDENCE, so no caller has to. */
function ShowEvidence({ on }: { on: OcrEvidenceDto }) {
    return on.via === 'adhoc' ? <AdHocEvidence on={on.reading} /> : <Evidence on={on.candidate} />;
}

function MatchRow({ m, only, onAttach }: { m: OcrMatchDto; only: boolean; onAttach: (id: string) => void }) {
    const when = m.scheduledStartUtc
        ? new Date(m.scheduledStartUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;
    return (
        <div className="flex items-start gap-2 rounded border p-2">
            <Truck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 text-xs">
                <div className="font-medium">
                    {m.appointmentNumber}
                    {m.direction ? ` · ${m.direction}` : ''}
                </div>
                <div className="text-muted-foreground">
                    {when ?? 'no time'}
                    {m.doorCode ? ` · door ${m.doorCode}` : ' · no door yet'}
                    {m.clientCode ? ` · ${m.clientCode}` : ''}
                    {m.carrierCode ? ` · ${m.carrierCode}` : ''}
                </div>
            </div>
            <Button size="sm" variant={only ? 'default' : 'outline'} onClick={() => onAttach(m.appointmentId)}>
                <Check className="h-3.5 w-3.5" />
                {only ? 'Attach' : 'This one'}
            </Button>
        </div>
    );
}

export function SuggestedLoadDialog({ open, result, documentName, onView, onAttach, onClose }: Props) {
    /**
     * ⛔ 'Unavailable' NEVER OPENS THIS DIALOG. OCR being unconfigured, slow or
     * rate-limited is not news for a scheduler with a driver at the desk — the
     * document is filed either way and the manual path is the one they already
     * use. The caller checks before opening; this is the second gate.
     */
    if (!result || result.kind === 'Unavailable') return null;

    const matched = result.kind === 'Matched';
    const only = matched && result.matches.length === 1;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {matched
                            ? (only ? 'This looks like one appointment' : `${result.matches.length} appointments match this page`)
                            : 'Nothing on today\'s schedule matches this page'}
                    </DialogTitle>
                    <DialogDescription className="truncate font-mono text-[11px]">
                        {documentName}
                    </DialogDescription>
                </DialogHeader>

                {matched && <ShowEvidence on={result.on} />}

                {matched ? (
                    <div className="max-h-[45vh] space-y-1.5 overflow-y-auto">
                        {result.matches.map(m => (
                            <MatchRow key={m.appointmentId} m={m} only={only} onAttach={onAttach} />
                        ))}
                    </div>
                ) : (
                    /**
                     * ⛔ "NOTHING MATCHED" IS NOT AN ERROR AND MUST NOT LOOK LIKE ONE.
                     * A walk-up with paperwork we cannot place is an ordinary morning.
                     * The page IS filed; only the shortcut was unavailable.
                     */
                    <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                        The document is filed. Attach the driver to an appointment the usual way.
                        {(result.candidates.length > 0 || result.adHocRead.length > 0) && (
                            <div className="mt-2 text-left text-[11px]">
                                <span className="text-muted-foreground">Read from the page, but not on today's board:</span>
                                <ul className="mt-0.5 space-y-0.5 font-mono">
                                    {result.candidates.slice(0, 5).map(c => (
                                        <li key={c.kind + c.value}>{KIND_LABEL[c.kind]} {c.value}</li>
                                    ))}
                                    {/**
                                      * ⚠️ THE CONFIGURED FIELDS ARE LISTED TOO. A screen that
                                      * reads 'Job No 44192' off a page and then says nothing
                                      * about it invites somebody to assume it was used — the
                                      * same reason `unmatchable` exists below.
                                      */}
                                    {result.adHocRead.slice(0, 5).map(a => (
                                        <li key={a.label + a.value}>{a.label} {a.value}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/**
                  * ⚠️ THE TWO THINGS THE PAGE OFFERED THAT WE CANNOT LOOK UP. A PRO
                  * and a trailer have no column on dock.Appointment (#247), so they are
                  * read and named rather than silently dropped — a screen that reads a
                  * number and says nothing about it invites somebody to assume it was
                  * used.
                  */}
                {result.unmatchable.length > 0 && (
                    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-700">
                        Also on the page but not searchable yet:{' '}
                        {result.unmatchable.map(c => `${KIND_LABEL[c.kind]} ${c.value}`).join(', ')}.
                    </div>
                )}

                {/**
                  * ⚠️ A MISREAD CONTAINER IS A DIFFERENT MESSAGE FROM AN UNKNOWN ONE.
                  * "Check the page" and "that container is not booked today" send a
                  * scheduler to two different places.
                  */}
                {result.containerMisreads.length > 0 && (
                    <div className="flex gap-2 rounded border border-amber-400 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                            Something container-shaped did not read cleanly
                            ({result.containerMisreads.join(', ')}). That is a scan quality
                            problem, not a missing booking.
                        </span>
                    </div>
                )}

                <DialogFooter>
                    <div className="flex w-full items-center justify-between gap-2">
                        {/* ⭐ THE EYE. This is what makes one click a decision. */}
                        <Button variant="outline" size="sm" onClick={onView}>
                            <Eye className="h-3.5 w-3.5" />
                            Look at the page
                        </Button>
                        {/* ⭐ "Not now" rather than "Cancel" — nothing is being undone. */}
                        <Button size="sm" variant="ghost" onClick={onClose}>Not now</Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
